"use client";

import { TenderOut } from "@/client";
import { MessageResponse } from "@/components/ai-elements/message";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { AlertCircleIcon, HelpCircleIcon, RefreshCcwIcon, Wand2Icon } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { toast } from "sonner";
interface ErrorPayload {
  error?: string;
  status?: string;
}
const QUOTA_REDIRECT_DELAY_MS = 1500;
type SummaryState = {
  kind: "idle";
} | {
  kind: "loading";
} | {
  kind: "streaming";
  questions: string[];
  text: string;
} | {
  kind: "ready";
  questions: string[];
  text: string;
} | {
  kind: "error";
  message: string;
  retryable: boolean;
};
export function TenderSummary({
  tender,
  isLoggedIn,
  fileStoreReady,
  disabled = false
}: {
  readonly tender: TenderOut;
  readonly isLoggedIn: boolean;
  readonly fileStoreReady: boolean;
  readonly disabled?: boolean;
}) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<SummaryState>({
    kind: "idle"
  });
  const generate = async () => {
    setState({
      kind: "loading"
    });
    try {
      const res = await fetch("/api/summary", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ulid: tender.ulid
        })
      });
      if (res.status === 429) {
        const body = (await res.json().catch(() => ({}))) as ErrorPayload;
        if (body.error === "QUOTA_EXCEEDED") {
          toast.error("Has excedido la cuota de tu plan", {
            description: "Has alcanzado el límite mensual de resúmenes. Te llevamos a los planes…"
          });
          setOpen(false);
          setTimeout(() => router.push("/pricing"), QUOTA_REDIRECT_DELAY_MS);
          return;
        }
      }
      if (res.status === 401) {
        router.push("/login");
        return;
      }
      if (res.status === 422) {
        setState({
          kind: "error",
          message: "Los documentos de la licitación aún se están procesando. Inténtalo de nuevo en unos minutos.",
          retryable: true
        });
        return;
      }
      if (!res.ok) {
        const body_0 = (await res.json().catch(() => ({}))) as ErrorPayload;
        setState({
          kind: "error",
          message: body_0.error ?? "No se pudo generar el resumen. Inténtalo de nuevo en unos minutos.",
          retryable: true
        });
        return;
      }
      if (!res.body) {
        setState({
          kind: "error",
          message: "Respuesta inválida del servidor.",
          retryable: true
        });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let questions: string[] = [];
      let accumulated = "";
      let streamError: string | null = null;
      let finished = false;
      while (!finished) {
        const {
          done,
          value
        } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, {
          stream: true
        });
        let newlineIdx;
        while ((newlineIdx = buffer.indexOf("\n")) >= 0) {
          const line = buffer.slice(0, newlineIdx).trim();
          buffer = buffer.slice(newlineIdx + 1);
          if (!line) continue;
          let event: {
            type: string;
            data?: unknown;
            message?: string;
          };
          try {
            event = JSON.parse(line) as typeof event;
          } catch {
            continue;
          }
          if (event.type === "questions" && Array.isArray(event.data)) {
            questions = event.data as string[];
            setState({
              kind: "streaming",
              questions,
              text: accumulated
            });
          } else if (event.type === "text" && typeof event.data === "string") {
            accumulated += event.data;
            setState({
              kind: "streaming",
              questions,
              text: accumulated
            });
          } else if (event.type === "error") {
            streamError = event.message ?? "Error durante la generación.";
            finished = true;
            break;
          } else if (event.type === "done") {
            finished = true;
            break;
          }
        }
      }
      if (streamError) {
        setState({
          kind: "error",
          message: streamError,
          retryable: true
        });
        return;
      }
      setState({
        kind: "ready",
        questions,
        text: accumulated
      });
    } catch (error) {
      console.error("Summary fetch failed:", error);
      setState({
        kind: "error",
        message: "Error de red al generar el resumen. Comprueba tu conexión e inténtalo de nuevo.",
        retryable: true
      });
    }
  };
  const handleOpenChange = (newOpen: boolean) => {
    if (!isLoggedIn && newOpen) {
      router.push("/login");
      return;
    }
    setOpen(newOpen);
    if (newOpen && state.kind === "idle") {
      void generate();
    }
  };
  const isReady = state.kind === "ready";
  const isStreaming = state.kind === "streaming";
  const hasContent = isReady || isStreaming;
  return <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <Button className="gap-2 bg-linear-to-r from-emerald-600 to-teal-600 text-white shadow-md transition-all hover:from-emerald-700 hover:to-teal-700 hover:shadow-lg disabled:from-emerald-400 disabled:to-teal-400" size="default" disabled={disabled || !fileStoreReady} title={!fileStoreReady ? "Los documentos aún no están procesados" : undefined}>
          <Wand2Icon className="size-4" />
          <span className="font-semibold">Resumen IA</span>
        </Button>
      </SheetTrigger>
      <SheetContent className="flex w-full flex-col sm:max-w-2xl">
        <SheetHeader className="shrink-0 border-b pb-4">
          <SheetTitle>Resumen generado por IA</SheetTitle>
          <SheetDescription className="line-clamp-2">
            {tender.title}
          </SheetDescription>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-4">
          {state.kind === "loading" && <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 text-center">
              <Wand2Icon className="size-10 animate-pulse text-emerald-600 dark:text-emerald-400" />
              <Shimmer duration={1.5}>Generando preguntas…</Shimmer>
              <p className="text-muted-foreground text-sm">
                Estamos analizando los pliegos. Las primeras preguntas
                aparecerán en unos segundos.
              </p>
            </div>}

          {state.kind === "error" && <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 text-center">
              <AlertCircleIcon className="size-10 text-red-600 dark:text-red-400" />
              <p className="text-sm">{state.message}</p>
              {state.retryable && <Button variant="outline" size="sm" onClick={() => void generate()} className="mt-2 gap-2">
                  <RefreshCcwIcon className="size-4" />
                  Reintentar
                </Button>}
            </div>}

          {hasContent && <div className="space-y-6">
              <section>
                <h3 className="mb-3 flex items-center gap-2 text-base font-semibold">
                  <HelpCircleIcon className="size-4 text-emerald-600 dark:text-emerald-400" />
                  Preguntas analizadas
                </h3>
                <ol className="text-muted-foreground list-decimal space-y-1.5 pl-6 text-sm">
                  {state.questions.map((q, i) => <li key={i}>{q}</li>)}
                </ol>
              </section>

              <section>
                <h3 className="mb-3 flex items-center gap-2 text-base font-semibold">
                  Resumen
                  {isStreaming && <Shimmer duration={1.2} className="text-xs font-normal">
                      generando…
                    </Shimmer>}
                </h3>
                {state.text ? <div className="prose prose-sm dark:prose-invert max-w-none">
                    <MessageResponse>{state.text}</MessageResponse>
                  </div> : <p className="text-muted-foreground text-sm italic">
                    Buscando en los documentos…
                  </p>}
              </section>
            </div>}
        </div>

        {isReady && <div className="bg-background/80 shrink-0 border-t p-4 backdrop-blur">
            <p className="text-muted-foreground text-xs">
              Generado por IA — verifica siempre la información en los pliegos
              oficiales.
            </p>
          </div>}
      </SheetContent>
    </Sheet>;
}